import React from "react";
import { Typography } from "@arco-design/web-react";
import { IconCheckCircleFill, IconExclamationCircle } from "@arco-design/web-react/icon";
import { useAuth0 } from "@auth0/auth0-react";
import { FetchApi } from "../../Api/AuthenticatedApi";

const { Title, Text, Paragraph } = Typography;

function Item({ done, title, children }) {
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
      {done ? (
        <IconCheckCircleFill style={{ color: "rgb(var(--green-6))", fontSize: 18, marginTop: 2, flexShrink: 0 }} />
      ) : (
        <IconExclamationCircle style={{ color: "rgb(var(--orange-6))", fontSize: 18, marginTop: 2, flexShrink: 0 }} />
      )}
      <div>
        <Text style={{ fontWeight: 600 }}>{title}</Text>
        <div style={{ color: "var(--color-text-2)", fontSize: 13, marginTop: 2 }}>{children}</div>
      </div>
    </div>
  );
}

// In-product deliverability checklist. The "handled" items are enforced by the
// backend; below them, how each of the org's sending domains is doing.
export default function DeliverabilityGuide() {
  const { getAccessTokenSilently } = useAuth0();
  // null until loaded; the org's sending domains after.
  const [domains, setDomains] = React.useState(null);
  React.useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/settings/sending-domains/",
      (data) => setDomains(data || []), () => {}, () => setDomains([]));
  }, [getAccessTokenSilently]);
  return (
    <div>
      <Paragraph type="secondary">
        Landing in the inbox (not spam) at Gmail, Outlook, Yahoo &amp; co. comes down
        to authenticating your sending domain and keeping your list clean. Here's where you stand.
      </Paragraph>

      <Title heading={6} style={{ marginTop: 8 }}>Handled for you</Title>
      <Item done title="Physical mailing address in every email">
        Auto-appended from your <b>Address</b> settings (CAN-SPAM requirement).
      </Item>
      <Item done title="One-click unsubscribe">
        Every send includes the <Text code>List-Unsubscribe</Text> +{" "}
        <Text code>List-Unsubscribe-Post</Text> headers Gmail/Yahoo require for bulk senders,
        plus an unsubscribe link in the footer.
      </Item>
      <Item done title="Bounce &amp; complaint suppression">
        Hard bounces and spam complaints are auto-suppressed so you stop mailing bad/angry
        addresses (keeping your complaint rate under the 0.3% threshold). See the Reports page.
      </Item>

      <Title heading={6} style={{ marginTop: 20 }}>Your sending domains</Title>
      <Paragraph type="secondary" style={{ fontSize: 13 }}>
        Mail providers trust mail that's signed as coming from your own domain. Add your domain
        under <b>Sending Domains</b> above and we'll show you the DNS records to add (DKIM, SPF and
        DMARC) and check them for you.
      </Paragraph>
      {domains === null ? null : domains.length === 0 ? (
        <Item title="Add your sending domain">
          You haven't added one yet. Without it, mail is harder to get into the inbox, and mail from
          Gmail or Yahoo addresses can't be sent as itself.
        </Item>
      ) : domains.map((d) => (
        <Item key={d.id} done={!!d.verified_date} title={d.domain}>
          {d.verified_date
            ? (d.dmarc_found
              ? "Verified, with a DMARC record. Any address on it can send."
              : "Verified. Any address on it can send. Adding a DMARC record (shown in its records) helps further.")
            : "Waiting for its DNS records. Open it under Sending Domains to see what's missing."}
        </Item>
      ))}
      <Item title="Tighten DMARC once mail passes">
        Start with <Text code>p=none</Text>. After a few weeks of passing mail, change your DMARC
        record to <Text code>p=quarantine</Text>, then <Text code>p=reject</Text>.
      </Item>
      <Item title="Warm up a new domain">
        Ramp volume up gradually so providers build a good reputation for it. A verified domain also
        covers its subdomains, so you can send from <Text code>news@mail.yourdomain.com</Text> to keep
        bulk mail apart from everyday mail.
      </Item>
    </div>
  );
}
